import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AppShell } from '@/components/layout/AppShell';
import { Toaster } from '@/components/ui/sonner';
import { Skeleton } from '@/components/ui/skeleton';

import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import PricingPage from '@/pages/PricingPage';
import StripeSuccessPage from '@/pages/StripeSuccessPage';
import ProfilePage from '@/pages/ProfilePage';
import DashboardPage from '@/pages/DashboardPage';
import VendorsPage from '@/pages/VendorsPage';
import VendorDetailPage from '@/pages/VendorDetailPage';
import PropertiesPage from '@/pages/PropertiesPage';
import PropertyDetailPage from '@/pages/PropertyDetailPage';
import DocumentsPage from '@/pages/DocumentsPage';
import DocumentDetailPage from '@/pages/DocumentDetailPage';
import CompliancePage from '@/pages/CompliancePage';
import ComplianceSettingsPage from '@/pages/ComplianceSettingsPage';
import CommunicationsPage from '@/pages/CommunicationsPage';
import TasksPage from '@/pages/TasksPage';
import ReportsPage from '@/pages/ReportsPage';
import SettingsPage from '@/pages/SettingsPage';
import VendorPortalPage from '@/pages/VendorPortalPage';
import BulkImportPage from '@/pages/BulkImportPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import SupportPage from '@/pages/SupportPage';
import SuperAdminLoginPage from '@/pages/SuperAdminLoginPage';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import GuidePage from '@/pages/GuidePage';
import DemoPage from '@/pages/DemoPage';
import GuidesIndexPage from '@/pages/GuidesIndexPage';
import GuideArticlePage from '@/pages/GuideArticlePage';
import ComparePage from '@/pages/ComparePage';
import CoiRequirementBuilderPage from '@/pages/CoiRequirementBuilderPage';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isSubscriptionActive, isAdmin, isSuperAdmin } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><Skeleton className="h-12 w-48" /></div>
  );
  if (!user) return <Navigate to="/login" />;
  // Super admin should go to their own dashboard, not the PM app
  if (isSuperAdmin) return <Navigate to="/superadmin" />;
  if (!isSubscriptionActive) return <Navigate to="/pricing" />;
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" />;
  return <AppShell>{children}</AppShell>;
};

const PublicRoute = ({ children }) => {
  const { user, loading, isSubscriptionActive, isSuperAdmin } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Skeleton className="h-12 w-48" /></div>;
  if (user && isSuperAdmin) return <Navigate to="/superadmin" />;
  if (user && isSubscriptionActive) return <Navigate to="/dashboard" />;
  return children;
};

const RootRoute = () => {
  const { user, loading, isSubscriptionActive, isSuperAdmin } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Skeleton className="h-12 w-48" /></div>;
  if (user && isSuperAdmin) return <Navigate to="/superadmin" />;
  if (user && isSubscriptionActive) return <Navigate to="/dashboard" />;
  if (user && !isSubscriptionActive) return <Navigate to="/pricing" />;
  return <LandingPage />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/guides" element={<GuidesIndexPage />} />
      <Route path="/guides/:slug" element={<GuideArticlePage />} />
      <Route path="/compare/vendorsuite-vs-netvendor" element={<ComparePage />} />
      <Route path="/tools/coi-requirement-builder" element={<CoiRequirementBuilderPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/stripe/success" element={<StripeSuccessPage />} />
      <Route path="/portal/:token" element={<VendorPortalPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Super Admin routes */}
      <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
      <Route path="/superadmin" element={<SuperAdminDashboard />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/" element={<RootRoute />} />
      <Route path="/vendors" element={<ProtectedRoute><VendorsPage /></ProtectedRoute>} />
      <Route path="/vendors/:id" element={<ProtectedRoute><VendorDetailPage /></ProtectedRoute>} />
      <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
      <Route path="/properties/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      <Route path="/documents/:id" element={<ProtectedRoute><DocumentDetailPage /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
      <Route path="/compliance/settings" element={<ProtectedRoute adminOnly><ComplianceSettingsPage /></ProtectedRoute>} />
      <Route path="/communications" element={<ProtectedRoute><CommunicationsPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>} />
      <Route path="/bulk-import" element={<ProtectedRoute adminOnly><BulkImportPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/guide" element={<ProtectedRoute><GuidePage /></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/landing" />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <TimezoneProviderWrapper>
            <AppRoutes />
            <Toaster position="top-right" />
          </TimezoneProviderWrapper>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

// Lazy timezone provider that only loads when authenticated
function TimezoneProviderWrapper({ children }) {
  const { TimezoneProvider } = require('@/lib/timezone');
  return <TimezoneProvider>{children}</TimezoneProvider>;
}

export default App;
